// @ts-check
// Detalhe do objeto (doc 11, 3.6.4): aba Campos (ativos, desativados e de sistema) e aba Configurações (sobre,
// identificador do registro, desativar e excluir).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Link, navigate } from '../../../app/router.js';
import { NotFoundPage } from '../../../app/not-found-page.js';
import { deleteObjectMetadata, updateObjectMetadata } from '../../../data/data-model.js';
import { getFieldIcon } from '../../../lib/field-metadata.js';
import { isTenantModuleEnabled } from '../../../data/metadata-store.js';
import { isHiddenModuleMetadata, withoutDisabledModuleMetadata } from '../../../lib/module-metadata-visibility.js';
import { normalizeSearchText } from '../../../lib/utils.js';
import { Button } from '../../../ui/button.js';
import { Tag } from '../../../ui/chip.js';
import { Icon } from '../../../ui/icon.js';
import { Toggle } from '../../../ui/feedback.js';
import { Modal } from '../../../ui/modal.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { TabList } from '../../../ui/tab-list.js';
import { t, translateMetadataLabel } from '../../../i18n/index.js';
import { PageCard, PageHeader } from '../../page/page-card.js';
import { SettingsSection } from '../settings-pages.js';
import {
  DangerZone, DataModelBreadcrumb, canEditDataModel, findObjectByNamePlural, rootBreadcrumbItem, showDataModelError,
} from './data-model-shared.js';
import { IconPicker } from './pickers.js';

/**
 * @typedef {import('../../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 */

const LABEL_IDENTIFIER_TYPES = ['TEXT', 'FULL_NAME', 'UUID', 'NUMBER', 'EMAILS', 'PHONES', 'LINKS'];
const DELETE_CONFIRMATION_WORD = 'excluir';

/** @param {FieldMetadata} field */
export const fieldKindLabel = (field) => (field.isSystem ? t('settings.objects.system') : field.isCustom ? t('settings.objects.custom') : t('settings.objects.standard'));

/**
 * @param {{ objectMetadata: ObjectMetadata, fields: FieldMetadata[] }} props
 */
function FieldsTable({ objectMetadata, fields }) {
  return html`<div class="settings-table">
    <div class="settings-table-row settings-table-header" data-columns="data-model-fields">
      <span>${t('settings.objects.columns.name')}</span>
      <span>${t('settings.objects.columns.apiName')}</span>
      <span>${t('settings.objects.columns.dataType')}</span>
      <span></span>
    </div>
    ${fields.map((field) => html`<${Link} key=${field.id} href=${`/settings/objects/${objectMetadata.namePlural}/${field.name}`} className="settings-table-row" data-columns="data-model-fields">
      <span class="settings-table-name"><${Icon} name=${getFieldIcon(field)} size=${16} />${translateMetadataLabel(field.label)}
        ${field.isActive === false ? html`<${Tag} color="gray" text=${t('dataModel.common.deactivated')} variant="outline" />` : null}</span>
      <span class="settings-table-muted settings-table-mono">${field.name}</span>
      <span class="settings-table-muted">${t(`fieldTypes.${field.type}`)} · ${fieldKindLabel(field)}</span>
      <span class="settings-table-chevron"><${Icon} name="IconChevronRight" size=${16} /></span>
    <//>`)}
  </div>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata }} props
 */
function ObjectFieldsTab({ objectMetadata }) {
  const [search, setSearch] = useState('');
  const [showSystem, setShowSystem] = useState(false);
  const normalizedSearch = normalizeSearchText(search);
  // campos de módulo desligado (Família, Membro da família) nem aparecem entre os inativos
  const fields = withoutDisabledModuleMetadata(objectMetadata.fields, isTenantModuleEnabled)
    .filter((field) => showSystem || !field.isSystem)
    .filter((field) => !normalizedSearch || normalizeSearchText(`${translateMetadataLabel(field.label)} ${field.name}`).includes(normalizedSearch))
    .sort((first, second) => translateMetadataLabel(first.label).localeCompare(translateMetadataLabel(second.label)));
  const active = fields.filter((field) => field.isActive !== false);
  const inactive = fields.filter((field) => field.isActive === false);
  return html`
    <${SettingsSection} title=${t('settings.objects.fieldsTitle')} description=${t('dataModel.fields.description', { object: translateMetadataLabel(objectMetadata.labelSingular) })}>
      <div class="data-model-toolbar">
        <label class="data-model-search">
          <${Icon} name="IconSearch" size=${16} />
          <input class="text-input" type="search" value=${search} placeholder=${t('dataModel.fields.search')} aria-label=${t('dataModel.fields.search')}
            onInput=${(event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        </label>
        <label class="data-model-toggle-label">
          <${Toggle} value=${showSystem} onChange=${setShowSystem} ariaLabel=${t('dataModel.fields.showSystem')} size="small" />
          <span>${t('dataModel.fields.showSystem')}</span>
        </label>
      </div>
      <${FieldsTable} objectMetadata=${objectMetadata} fields=${active} />
    <//>
    ${inactive.length > 0
      ? html`<${SettingsSection} title=${t('dataModel.fields.inactiveTitle')} description=${t('dataModel.fields.inactiveDescription')}>
          <${FieldsTable} objectMetadata=${objectMetadata} fields=${inactive} />
        <//>`
      : null}
  `;
}

/**
 * @param {{ objectMetadata: ObjectMetadata }} props
 */
function ObjectSettingsTab({ objectMetadata }) {
  const isEditable = canEditDataModel();
  const [icon, setIcon] = useState(objectMetadata.icon ?? 'IconCube');
  const [labelSingular, setLabelSingular] = useState(translateMetadataLabel(objectMetadata.labelSingular));
  const [labelPlural, setLabelPlural] = useState(translateMetadataLabel(objectMetadata.labelPlural));
  const [description, setDescription] = useState(translateMetadataLabel(objectMetadata.description ?? ''));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const labelFields = objectMetadata.fields.filter((field) => LABEL_IDENTIFIER_TYPES.includes(field.type) && field.isActive !== false && !field.isSystem);

  const isDirty = icon !== (objectMetadata.icon ?? 'IconCube')
    || labelSingular !== translateMetadataLabel(objectMetadata.labelSingular)
    || labelPlural !== translateMetadataLabel(objectMetadata.labelPlural)
    || description !== translateMetadataLabel(objectMetadata.description ?? '');

  /** @param {Record<string, unknown>} patch @param {string} successMessage */
  const patchObject = async (patch, successMessage) => {
    setIsSaving(true);
    try {
      await updateObjectMetadata(objectMetadata.id, patch);
      showSuccessSnackbar(successMessage);
    } catch (error) {
      showDataModelError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const saveAbout = () => {
    /** @type {Record<string, unknown>} */
    const patch = { icon, description: description.trim() || null };
    // rótulo padrão traduzido só é gravado quando a escola o muda de fato
    if (labelSingular !== translateMetadataLabel(objectMetadata.labelSingular)) patch.labelSingular = labelSingular.trim();
    if (labelPlural !== translateMetadataLabel(objectMetadata.labelPlural)) patch.labelPlural = labelPlural.trim();
    patchObject(patch, t('dataModel.common.saved'));
  };

  const removeObject = async () => {
    setIsSaving(true);
    try {
      await deleteObjectMetadata(objectMetadata.id);
      showSuccessSnackbar(t('dataModel.objects.deleted'));
      navigate('/settings/objects', { replace: true });
    } catch (error) {
      showDataModelError(error);
      setIsSaving(false);
    }
  };

  return html`
    <${SettingsSection} title=${t('dataModel.objects.aboutTitle')} description=${t('dataModel.objects.aboutDescription')}>
      <div class="data-model-name-row">
        <${IconPicker} value=${icon} onChange=${setIcon} disabled=${!isEditable} />
        <label class="form-field">
          <span class="form-field-label">${t('dataModel.objects.labelSingular')}</span>
          <input class="text-input" value=${labelSingular} disabled=${!isEditable} maxlength="255"
            onInput=${(event) => setLabelSingular(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        </label>
        <label class="form-field">
          <span class="form-field-label">${t('dataModel.objects.labelPlural')}</span>
          <input class="text-input" value=${labelPlural} disabled=${!isEditable} maxlength="255"
            onInput=${(event) => setLabelPlural(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        </label>
      </div>
      <label class="form-field">
        <span class="form-field-label">${t('dataModel.common.description')}</span>
        <textarea class="text-input data-model-textarea" value=${description} disabled=${!isEditable} placeholder=${t('dataModel.common.descriptionPlaceholder')}
          onInput=${(event) => setDescription(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
      </label>
      <dl class="data-model-api-names">
        <div><dt>${t('dataModel.objects.apiNameSingular')}</dt><dd class="settings-table-mono">${objectMetadata.nameSingular}</dd></div>
        <div><dt>${t('dataModel.objects.apiNamePlural')}</dt><dd class="settings-table-mono">${objectMetadata.namePlural}</dd></div>
      </dl>
      ${isEditable
        ? html`<div><${Button} title=${t('common.save')} icon="IconCheck" accent="blue" size="small" disabled=${!isDirty || !labelSingular.trim() || !labelPlural.trim()}
            isLoading=${isSaving} onClick=${saveAbout} /></div>`
        : null}
    <//>
    <${SettingsSection} title=${t('dataModel.objects.identifierTitle')} description=${t('dataModel.objects.identifierDescription')}>
      <label class="form-field">
        <span class="form-field-label">${t('dataModel.objects.recordLabel')}</span>
        <select class="text-input data-model-select" value=${objectMetadata.labelIdentifierFieldMetadataId ?? ''} disabled=${!isEditable || isSaving}
          onChange=${(event) => patchObject({ labelIdentifierFieldMetadataId: /** @type {HTMLSelectElement} */ (event.currentTarget).value }, t('dataModel.common.saved'))}>
          ${labelFields.map((field) => html`<option key=${field.id} value=${field.id}>${translateMetadataLabel(field.label)}</option>`)}
        </select>
      </label>
    <//>
    ${isEditable
      ? html`<${DangerZone} title=${t('dataModel.common.dangerZone')} description=${objectMetadata.isCustom ? t('dataModel.objects.dangerCustom') : t('dataModel.objects.dangerStandard')}>
          ${objectMetadata.isActive === false
            ? html`<${Button} title=${t('dataModel.common.activate')} icon="IconEye" variant="secondary" size="small" isLoading=${isSaving}
                onClick=${() => patchObject({ isActive: true }, t('dataModel.objects.activated'))} />`
            : html`<${Button} title=${t('dataModel.common.deactivate')} icon="IconEyeOff" variant="secondary" size="small" isLoading=${isSaving}
                onClick=${() => patchObject({ isActive: false }, t('dataModel.objects.deactivated'))} />`}
          ${objectMetadata.isCustom
            ? html`<${Button} title=${t('dataModel.common.delete')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setIsDeleteOpen(true)} />`
            : null}
        <//>`
      : null}
    ${isDeleteOpen
      ? html`<${Modal} title=${t('dataModel.objects.deleteTitle', { object: translateMetadataLabel(objectMetadata.labelSingular) })}
          subtitle=${t('dataModel.objects.deleteSubtitle', { word: DELETE_CONFIRMATION_WORD })} confirmLabel=${t('dataModel.common.delete')} confirmAccent="danger"
          isConfirmDisabled=${confirmation.trim().toLowerCase() !== DELETE_CONFIRMATION_WORD} isLoading=${isSaving}
          onClose=${() => setIsDeleteOpen(false)} onConfirm=${removeObject}>
          <input class="text-input" value=${confirmation} placeholder=${DELETE_CONFIRMATION_WORD} autofocus aria-label=${t('dataModel.common.typeToConfirm')}
            onInput=${(event) => setConfirmation(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        <//>`
      : null}
  `;
}

/**
 * @param {{ objectNamePlural: string }} props
 */
export function SettingsObjectDetailPage({ objectNamePlural }) {
  const objectMetadata = findObjectByNamePlural(objectNamePlural);
  const [activeTab, setActiveTab] = useState('fields');
  useEffect(() => {
    if (objectMetadata) document.title = `${translateMetadataLabel(objectMetadata.labelPlural)} - ${t('navigation.settings')}`;
  }, [objectMetadata?.id]);
  if (!objectMetadata || isHiddenModuleMetadata(objectMetadata, isTenantModuleEnabled)) return html`<${NotFoundPage} />`;
  const breadcrumb = html`<${DataModelBreadcrumb} items=${[rootBreadcrumbItem(), { label: translateMetadataLabel(objectMetadata.labelPlural) }]} />`;
  const actions = html`<div class="data-model-header-actions">
    ${objectMetadata.isActive !== false && !objectMetadata.isSystem
      ? html`<${Button} title=${t('dataModel.objects.seeRecords')} icon="IconArrowRight" variant="secondary" size="small" onClick=${() => navigate(`/objects/${objectMetadata.namePlural}`)} />`
      : null}
    ${canEditDataModel() && activeTab === 'fields'
      ? html`<${Button} title=${t('dataModel.fields.add')} icon="IconPlus" accent="blue" size="small" onClick=${() => navigate(`/settings/objects/${objectMetadata.namePlural}/new-field`)} />`
      : null}
  </div>`;
  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} actions=${actions} />`}>
    <div class="settings-page">
      <div class="data-model-object-title">
        <${Icon} name=${objectMetadata.icon ?? 'IconCube'} size=${20} />
        <h1>${translateMetadataLabel(objectMetadata.labelPlural)}</h1>
        <${Tag} color=${objectMetadata.isCustom ? 'blue' : 'gray'} text=${objectMetadata.isSystem ? t('settings.objects.system') : objectMetadata.isCustom ? t('settings.objects.custom') : t('settings.objects.standard')} />
        ${objectMetadata.isActive === false ? html`<${Tag} color="gray" text=${t('dataModel.common.deactivated')} variant="outline" />` : null}
      </div>
      <${TabList} activeTabId=${activeTab} onChange=${setActiveTab} tabs=${[
        { id: 'fields', title: t('settings.objects.fieldsTitle'), icon: 'IconList' },
        { id: 'settings', title: t('dataModel.objects.settingsTab'), icon: 'IconSettings' },
      ]} />
      ${activeTab === 'fields'
        ? html`<${ObjectFieldsTab} objectMetadata=${objectMetadata} />`
        : html`<${ObjectSettingsTab} key=${objectMetadata.id} objectMetadata=${objectMetadata} />`}
    </div>
  <//>`;
}
